'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { AprAllocation, earnSummaryBunja, getEarnSummaryOfCurrency } from '@flipster/features/src/shared/earn/state';
import { FormattedMessage } from '@flipster/i18n';
import { useOverlay } from '@flipster/overlay';
import { Tooltip } from '@flipster/react/design-system-legacy/tooltip';
import { IconButton } from '@flipster/react/design-system/icon-button';
import { Modal } from '@flipster/react/design-system/modal';
import { TextButton } from '@flipster/react/design-system/text-button';
import Formatted from '@flipster/react/design-utils/formatted';
import ChevronDownIcon from '@flipster/react/icon/generated/ChevronDownIcon';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, square, stack } from '@flipster/styled-system/patterns';
import { alert } from '@flipster/ui/Alert';
import {
  NonUsdCurrencyList,
  NonUsdPrecision,
  Usd1Currency,
  UsdPrecision,
  UsdtCurrency,
  UsdtCurrencyList,
} from '@flipster/utils/constants/currency';
import { BigNumber } from '@flipster/utils/num';

export const AprAppliedBalance = ({ currency }: { currency: string }) => {
  const overlay = useOverlay();
  const { earnSummaryAtom } = useBunja(earnSummaryBunja);
  const earnSummary = useAtomValue(earnSummaryAtom);

  const { isBalanceEmpty, allocations, noAprAppliedBalance } = getEarnSummaryOfCurrency(currency, earnSummary);

  const getTooltipContents = () => {
    if ([...NonUsdCurrencyList, Usd1Currency].includes(currency)) {
      return (
        <ul className={css({ list: 'disc' })}>
          <li>
            <FormattedMessage id='Autogen_common.r251126.s_Balance_used_for_daily_earnings_calculation' />
          </li>
          <li>
            <FormattedMessage id='Autogen_common.r251126.s_Includes_unrealized_PnL_updated_on_every_entrance_to_earn_page' />
          </li>
          <li>
            <FormattedMessage id='Autogen_common.r251126.s_Calculated_based_on_earn_limit_for_each_APR_category' />
          </li>
        </ul>
      );
    }
    return (
      <ul className={css({ list: 'disc' })}>
        <li>
          <FormattedMessage id='Autogen_common.r251126.s_Balance_used_for_daily_earnings_calculation' />
        </li>
        <li>
          <FormattedMessage id='Autogen_common.r251126.s_Includes_unrealized_PnL_updated_on_every_entrance_to_earn_page' />
        </li>
        <li>
          <FormattedMessage id='Earn.r260220.Pusdt_usde_apr_applied_balance_tooltip_2' />
        </li>
        <li>
          <FormattedMessage id='Autogen_common.r251126.s_Calculated_based_on_earn_limit_for_each_APR_category' />
        </li>
      </ul>
    );
  };

  return (
    <details className={css({ borderTopWidth: '1px', borderTopStyle: 'solid', borderColor: 'border.default.level-1' })}>
      <summary
        className={hstack({
          h: 48,
          cursor: isBalanceEmpty ? 'default' : 'pointer',
          userSelect: 'none',
          justify: 'space-between',
        })}
      >
        <div className={hstack()}>
          <span
            className={css({
              textStyle: 'Text/M/Regular',
              color: 'content.default.level-5',
            })}
          >
            <FormattedMessage id='Autogen_common.r251126.s_APR_applied_balance' />
          </span>
          <Tooltip
            placement='bottom'
            crossAxisOffset={75}
            maxWidth={500}
            mobileAlert={() => {
              alert.show({
                title: 'APR applied balance',
                message: getTooltipContents(),
                confirmText: 'OK',
              });
            }}
          >
            <Tooltip.Trigger asChild>
              <IconButton icon={<InformationCircleIcon />} size='small' hierarchy='quaternary' />
            </Tooltip.Trigger>
            <Tooltip.Content>{getTooltipContents()}</Tooltip.Content>
          </Tooltip>
        </div>
        {isBalanceEmpty ? (
          <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>
            <FormattedMessage id='Autogen_common.r251126.s_None' />
          </span>
        ) : (
          <span
            className={square({
              size: 24,
              color: 'content.default.level-3',
              '[open] &': { transform: 'rotate(-180deg)' },
            })}
          >
            <ChevronDownIcon size={16} />
          </span>
        )}
      </summary>
      {!isBalanceEmpty && (
        <div className={stack({ pb: 8 })}>
          {allocations.map((allocation, i) => (
            <div key={i} className={hstack({ h: 28, justify: 'space-between' })}>
              <TextButton
                label={
                  <FormattedMessage
                    id='Common.r251008.Apr_percent_apr'
                    values={{
                      apr: allocation.sumOfAppliedApr.multipliedBy(100).toString(),
                    }}
                  />
                }
                color='passive'
                size='small'
                onClick={() =>
                  overlay.open(({ isOpen, onClose, unmount }) => (
                    <AllocationModal allocation={allocation} isOpen={isOpen} onClose={onClose} unmount={unmount} />
                  ))
                }
              />
              <Formatted.Number
                value={allocation.allocatedBalance}
                precision={NonUsdCurrencyList.includes(currency) ? NonUsdPrecision : UsdPrecision}
                suffix={UsdtCurrencyList.includes(currency) ? UsdtCurrency : currency}
                toFixed
                css={{ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-5' }}
              />
            </div>
          ))}
          {noAprAppliedBalance && <NoAprApplied value={noAprAppliedBalance} currency={currency} />}
        </div>
      )}
    </details>
  );
};

const NoAprApplied = ({ value, currency }: { value: BigNumber; currency: string }) => {
  const overlay = useOverlay();

  return (
    <div className={hstack({ h: 28, justify: 'space-between' })}>
      <TextButton
        label={<FormattedMessage id='Autogen_common.r251126.s_No_APR_applied' />}
        color='passive'
        size='small'
        onClick={() =>
          overlay.open(({ isOpen, onClose, unmount }) => (
            <NoAprAppliedModal isOpen={isOpen} onClose={onClose} unmount={unmount} />
          ))
        }
      />
      <Formatted.Number
        value={value}
        precision={NonUsdCurrencyList.includes(currency) ? NonUsdPrecision : UsdPrecision}
        suffix={UsdtCurrencyList.includes(currency) ? UsdtCurrency : currency}
        toFixed
        css={{ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-5' }}
      />
    </div>
  );
};

const AllocationModal = ({
  allocation,
  isOpen,
  onClose,
  unmount,
}: {
  allocation: AprAllocation;
  isOpen: boolean;
  onClose: () => void;
  unmount: () => void;
}) => {
  return (
    <Modal.Root isOpen={isOpen} onClose={onClose} onExit={unmount}>
      <Modal.Header>
        <Modal.Title
          label={
            <FormattedMessage
              id='Common.r251008.Apr_percent_apr'
              values={{ apr: allocation.sumOfAppliedApr.multipliedBy(100).toString() }}
            />
          }
        />
      </Modal.Header>
      <Modal.Content>
        {allocation.aprs.map((apr) => (
          <div
            key={apr.title}
            className={hstack({
              justifyContent: 'space-between',
              py: 12,
              borderColor: 'border.default.level-1',
              borderBottomWidth: '1px',
              borderBottomStyle: 'solid',
            })}
          >
            <p className={css({ textStyle: 'Text/M/Strong' })}>{apr.title}</p>
            <Formatted.Percent value={apr.appliedApr} isRate />
          </div>
        ))}
      </Modal.Content>
      <Modal.Footer>
        <Modal.Button size='large' label={<FormattedMessage id='Common.r250401.Dismiss' />} onClick={onClose} />
      </Modal.Footer>
    </Modal.Root>
  );
};

const NoAprAppliedModal = ({
  isOpen,
  onClose,
  unmount,
}: {
  isOpen: boolean;
  onClose: () => void;
  unmount: () => void;
}) => {
  return (
    <Modal.Root isOpen={isOpen} onClose={onClose} onExit={unmount}>
      <Modal.Header>
        <Modal.Title label={<FormattedMessage id='Earn.r260120.Part_of_your_balance_is_not_eanring_apr' />} />
      </Modal.Header>
      <Modal.Content>
        <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
          <FormattedMessage id='Earn.r260120.Not_all_of_your_balance_is_earning_apr' />
          <br />
          <FormattedMessage id='Earn.r260120.Here_is_why_colon' />
        </p>
        <div className={css({ pt: 20 })}>
          <div
            className={stack({
              gap: 8,
              py: 16,
              borderTopWidth: '1px',
              borderTopStyle: 'solid',
              borderColor: 'border.default.level-1',
            })}
          >
            <p className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-5' })}>
              <FormattedMessage id='Earn.r260120.Apr_earning_limits' />
            </p>
            <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
              <FormattedMessage id='Earn.r260120.Apr_earning_limits_description' />
            </p>
          </div>
        </div>
      </Modal.Content>
    </Modal.Root>
  );
};
